/**
 * Criar uma empresa — um passo só, e o resto é do assistente.
 *
 * Até 2026-10-06 isto era um wizard de cinco passos que COLETAVA razão
 * social, CNPJ, país, fuso e logo sem ter onde guardar (o backend não
 * tem essas colunas), buscava logo num endpoint que não existe
 * (`/companies/logos/search`, o erro do passo 3 em toda empresa nova) e
 * subia o arquivo numa rota do backend descartado. O que sobrevive é o
 * que `POST /companies` aceita: nome, marca principal com termos e as
 * tags de citação — e o "Preencher com IA" continua alimentando as tags,
 * pelo mapeamento de `create-company-payload.ts`.
 *
 * Criada a empresa, a tela leva pro assistente de configuração
 * (`/empresas/:id/configuracoes/assistente?nova=1`), que é onde escala,
 * logos e o cadastro de classificação acontecem — sob o `X-Company-Id`
 * da empresa recém-criada, que a casca publica a partir da URL.
 */

import {
  Building2,
  Check,
  FileSpreadsheet,
  LoaderCircle,
  Sparkles,
  UserRound,
  WandSparkles
} from "lucide-react";
import { FileDrop } from "@/shared/components/FileDrop";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ApiError,
  apiClient,
  type CompanyContext,
  type CompanyFromSpreadsheet,
  type CompanyGroup,
  type CreatedCompany
} from "@/shared/api/api-client";
import { Select } from "@/shared/components/Select";
import { errorMessage } from "@/shared/api/http";
import { ConfigSources } from "@/features/companies/ConfigSources";
import { buildCreateCompanyPayload } from "@/features/companies/create-company-payload";

type CompanyOnboardingProps = {
  token: string;
  currentUserName: string;
  /** As empresas que a pessoa acessa (o `/me` da casca): de onde sai a
   * configuração a copiar. */
  companies?: CompanyContext[];
  onCancel: () => void;
  onCreated: (company: CreatedCompany) => Promise<void> | void;
  /** Quem cria é o Máster, que pode deixar a empresa sem núcleo (vincula
   *  depois). O Administrador escolhe um dos núcleos dele (RN-01). */
  master?: boolean;
};

export function CompanyOnboarding({ token, currentUserName, companies = [], onCancel, onCreated, master = false }: CompanyOnboardingProps) {
  const navigate = useNavigate();
  // "Do zero" é o formulário de sempre; "Por planilha" funda a empresa a
  // partir da planilha de configuração (a que "Baixar configuração" gera).
  const [mode, setMode] = useState<"scratch" | "spreadsheet">("scratch");
  const [file, setFile] = useState<File | null>(null);
  // Empresa criada, importação recusada pela fila: fica na tela com o
  // aviso (navegar de imediato o esconderia) e o botão de seguir.
  const [importNotice, setImportNotice] = useState<{ id: string; message: string } | null>(null);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [customBrandName, setCustomBrandName] = useState(false);
  const [brandNameOverride, setBrandNameOverride] = useState("");
  const [brandTerms, setBrandTerms] = useState("");
  const [aliases, setAliases] = useState("");
  const [slogans, setSlogans] = useState("");
  const [commonNames, setCommonNames] = useState("");
  const [products, setProducts] = useState("");
  const [initiatives, setInitiatives] = useState("");
  const [campaigns, setCampaigns] = useState("");
  const [autofilling, setAutofilling] = useState(false);
  const [autofillNotice, setAutofillNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // A empresa já existe no servidor mas a casca não conseguiu relê-la:
  // criar de novo só tomaria o 409 de nome repetido.
  const [created, setCreated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // O núcleo em que a empresa nasce (RN-01, 2026-10-22). A lista só é
  // pedida quando a pessoa abre o campo: a tela abre sem chamar a API
  // (teste de render). Sem escolha, quem decide é o servidor — o único
  // núcleo do Administrador, ou "Escolha o núcleo" se ele tiver vários; o
  // Máster pode deixar sem e vincular depois em Núcleos.
  const [groups, setGroups] = useState<CompanyGroup[] | null>(null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const loadGroups = () => {
    if (groups !== null) return;
    setGroups([]);
    apiClient
      .listCompanyGroups(token)
      .then(rows => setGroups(rows.filter(group => group.is_active)))
      .catch(() => setGroups(null));
  };

  const brandName = customBrandName ? brandNameOverride : name.trim();

  const autofillWithAi = async () => {
    if (name.trim().length < 2) return;
    setAutofilling(true);
    setError(null);
    setAutofillNotice(null);
    try {
      const s = await apiClient.autofillCompany(token, name.trim(), website);
      if (s.website && !website.trim()) setWebsite(s.website);
      if (s.aliases.length && !aliases.trim()) setAliases(s.aliases.join("; "));
      if (s.slogans.length && !slogans.trim()) setSlogans(s.slogans.join("; "));
      if (s.common_names.length && !commonNames.trim()) setCommonNames(s.common_names.join("; "));
      if (s.products.length && !products.trim()) setProducts(s.products.join("; "));
      if (s.initiatives.length && !initiatives.trim()) setInitiatives(s.initiatives.join("; "));
      if (s.campaigns.length && !campaigns.trim()) setCampaigns(s.campaigns.join("; "));
      setAutofillNotice("✓ Campos preenchidos pela IA — revise antes de criar.");
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 503
          ? "Preenchimento por IA não está configurado no ambiente."
          : errorMessage(caught, "Não foi possível preencher com IA.")
      );
    } finally {
      setAutofilling(false);
    }
  };

  const createFromSpreadsheet = async () => {
    if (!file) {
      setError("Escolha a planilha de configuração (.xlsx).");
      return;
    }
    setSaving(true);
    let result: CompanyFromSpreadsheet;
    try {
      result = groupId === null
        ? await apiClient.createCompanyFromSpreadsheet(token, name.trim(), file)
        : await apiClient.createCompanyFromSpreadsheet(token, name.trim(), file, groupId);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 409
          ? "Já existe uma empresa com esse nome."
          : errorMessage(caught, "Não foi possível criar a empresa a partir da planilha.")
      );
      setSaving(false);
      return;
    }
    // Mesma regra do caminho normal: depois do POST dar certo, a falha da
    // releitura é outra coisa — a empresa existe.
    try {
      // O servidor devolve só {id, name}, como no `POST /companies`.
      await onCreated(result as unknown as CreatedCompany);
    } catch {
      setCreated(true);
      setError(
        "A empresa foi criada, mas a lista não pôde ser atualizada. Recarregue a página e abra a empresa para configurá-la."
      );
      setSaving(false);
      return;
    }
    if (result.import_error) {
      setCreated(true);
      setImportNotice({
        id: result.id,
        message: `Empresa criada, mas a importação não pôde ser enviada (${result.import_error}). Envie a planilha pela aba Importar configuração.`
      });
      setSaving(false);
      return;
    }
    navigate(`/empresas/${result.id}/configuracoes?tab=importar-configuracao`);
  };

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Informe o nome da empresa.");
      return;
    }
    if (mode === "spreadsheet") {
      await createFromSpreadsheet();
      return;
    }
    if (customBrandName && !brandNameOverride.trim()) {
      setError("Informe o nome da marca principal.");
      return;
    }
    setSaving(true);
    const payload = {
      ...buildCreateCompanyPayload({
        name,
        brandName,
        aliases,
        commonNames,
        slogans,
        products,
        initiatives,
        campaigns
      }),
      ...(brandTerms.trim() ? { main_brand_terms: brandTerms.trim() } : {}),
      ...(groupId !== null ? { group_id: groupId } : {})
    };
    let company: CreatedCompany;
    try {
      company = await apiClient.createCompany(token, payload);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 409
          ? "Já existe uma empresa com esse nome."
          : caught instanceof ApiError && caught.status === 422
            ? errorMessage(caught, "Os termos da marca não são uma expressão válida.")
            : errorMessage(caught, "Não foi possível cadastrar a empresa.")
      );
      setSaving(false);
      return;
    }
    // Era bug (consertado em 2026-10-09, teste de render): a releitura do
    // `/me` morava no mesmo `try` do POST, então a falha dela (rede, 429)
    // era reportada como "Não foi possível cadastrar a empresa" com a
    // empresa JÁ criada — e a segunda tentativa tomava o 409 de nome
    // repetido. Depois do POST dar certo, a falha da releitura é outra
    // coisa: diz que foi criada e trava o botão de criar.
    try {
      // Primeiro a casca fica sabendo da empresa (é ela que publica o
      // `X-Company-Id` pela URL); só então a rota do assistente resolve.
      await onCreated(company);
      navigate(`/empresas/${company.id}/configuracoes/assistente?nova=1`);
    } catch {
      setCreated(true);
      setError(
        "A empresa foi criada, mas a lista não pôde ser atualizada. Recarregue a página e abra a empresa para configurá-la."
      );
      setSaving(false);
    }
  };

  return (
    <section className="company-onboarding" aria-labelledby="company-onboarding-title">
      {/* O voltar e o título ficam na barra do topo ("← Nova empresa"),
          como nas outras telas (2026-10-16); aqui, só a descrição. */}
      <header className="onboarding-header">
        <h1 id="company-onboarding-title" className="sr-only">Criar empresa</h1>
        <p>
          Só o essencial aqui. Em seguida o assistente monta o cadastro de classificação com
          ajuda da IA.
        </p>
      </header>

      <div className="onboarding-panel">
        <form onSubmit={create}>
          <div className="onboarding-fields onboarding-group">
            <label className="field">
              <span>Núcleo</span>
              <Select
                value={groupId === null ? "" : String(groupId)}
                onFocus={loadGroups}
                onPointerDown={loadGroups}
                onChange={event => setGroupId(event.target.value ? Number(event.target.value) : null)}
                disabled={saving || created}
              >
                <option value="">{master ? "Sem núcleo (vincular depois)" : "O seu núcleo"}</option>
                {(groups ?? []).map(group => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </Select>
              <small>
                {master
                  ? "Toda empresa pertence a um núcleo, e quem está nele enxerga a empresa."
                  : "A empresa nasce num núcleo seu. Se você tem mais de um, escolha qual."}
              </small>
            </label>
          </div>
          <div className="onboarding-mode" role="radiogroup" aria-label="Como criar a empresa">
            <label className={`onboarding-mode-card${mode === "scratch" ? " is-checked" : ""}`}>
              <input
                type="radio"
                name="creation-mode"
                checked={mode === "scratch"}
                onChange={() => setMode("scratch")}
                disabled={saving || created}
              />
              <span className="onboarding-mode-icon" aria-hidden="true">
                <Sparkles size={18} />
              </span>
              <span className="onboarding-mode-copy">
                <strong>Criar do zero</strong>
                <small>Você informa o nome e como a marca é citada; o assistente monta o resto com IA.</small>
              </span>
            </label>
            <label className={`onboarding-mode-card${mode === "spreadsheet" ? " is-checked" : ""}`}>
              <input
                type="radio"
                name="creation-mode"
                checked={mode === "spreadsheet"}
                onChange={() => setMode("spreadsheet")}
                disabled={saving || created}
              />
              <span className="onboarding-mode-icon" aria-hidden="true">
                <FileSpreadsheet size={18} />
              </span>
              <span className="onboarding-mode-copy">
                <strong>Criar a partir de uma planilha de configuração</strong>
                <small>Copie o cadastro de outra empresa ou preencha o modelo — tudo entra de uma vez.</small>
              </span>
            </label>
          </div>

          {mode === "spreadsheet" ? (
            <ol className="onboarding-sheet-steps">
              <li>
                <span className="admin-step-num" aria-hidden="true">1</span>
                <div className="onboarding-sheet-step-body">
                  <h3>Dê um nome à empresa</h3>
                  <div className="onboarding-fields">
                    <label className="field">
                      <span>Nome da empresa <b aria-hidden="true">*</b></span>
                      <input
                        required
                        autoFocus
                        maxLength={180}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Ex.: Acme Energia"
                      />
                      <small>Vale o nome digitado aqui, mesmo que a planilha traga outro.</small>
                    </label>
                  </div>
                </div>
              </li>
              <li>
                <span className="admin-step-num" aria-hidden="true">2</span>
                <div className="onboarding-sheet-step-body">
                  <h3>Pegue uma planilha</h3>
                  <p>Se ainda não tem uma, baixe por aqui e edite no Excel antes de enviar.</p>
                  <ConfigSources token={token} companies={companies} />
                </div>
              </li>
              <li>
                <span className="admin-step-num" aria-hidden="true">3</span>
                <div className="onboarding-sheet-step-body">
                  <h3>Envie a planilha</h3>
                  <FileDrop
                    label="Planilha de configuração (.xlsx)"
                    required
                    accept=".xlsx"
                    files={file ? [file] : []}
                    onChange={files => setFile(files[0] ?? null)}
                    formats="Arquivo .xlsx"
                    help="Marca principal, termos e todo o cadastro vêm dela; a importação começa assim que a empresa for criada."
                  />
                </div>
              </li>
            </ol>
          ) : (
          <>
          <div className="onboarding-panel-title">
            <Building2 size={21} />
            <div>
              <h2>Como a empresa é conhecida?</h2>
              <p>O nome da empresa, a marca principal e os termos que a identificam.</p>
            </div>
            <button
              className="secondary-button ai-fill-button"
              type="button"
              onClick={() => void autofillWithAi()}
              disabled={autofilling || name.trim().length < 2}
              title={
                name.trim().length < 2
                  ? "Digite o nome da empresa para a IA sugerir o resto"
                  : "Preencher as listas de citação com IA"
              }
            >
              {autofilling ? <LoaderCircle className="spin" size={16} /> : <WandSparkles size={16} />}
              {autofilling ? "Preenchendo…" : "Preencher com IA"}
            </button>
          </div>

          {autofillNotice && <p className="onboarding-tip"><span>{autofillNotice}</span></p>}

          <div className="onboarding-fields">
            <label className="field">
              <span>Nome da empresa <b aria-hidden="true">*</b></span>
              <input
                required
                autoFocus
                maxLength={180}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Ex.: Acme Energia"
              />
            </label>
            <label className="field">
              <span>Site</span>
              <input
                value={website}
                onChange={e => setWebsite(e.target.value)}
                placeholder="empresa.com.br"
              />
              <small>Só ajuda o preenchimento por IA; não é guardado.</small>
            </label>

            <label className="field">
              <span>Marca principal</span>
              {customBrandName ? (
                <input
                  maxLength={180}
                  value={brandNameOverride}
                  onChange={e => setBrandNameOverride(e.target.value)}
                />
              ) : (
                <div className="field-static" title="Igual ao nome da empresa">{name.trim() || "—"}</div>
              )}
              <button
                className="text-button"
                type="button"
                onClick={() => {
                  if (customBrandName) setCustomBrandName(false);
                  else {
                    setBrandNameOverride(name);
                    setCustomBrandName(true);
                  }
                }}
              >
                {customBrandName ? "Usar o nome da empresa" : "A marca tem nome diferente?"}
              </button>
            </label>
            <label className="field">
              <span>Termos da marca</span>
              <input
                value={brandTerms}
                onChange={e => setBrandTerms(e.target.value)}
                placeholder={'Ex.: "Acme Energia" OR Acme'}
              />
              <small>Opcional — o assistente sugere a partir das publicações.</small>
            </label>
          </div>

          <div className="onboarding-subsection">
            <h3>Como a marca é citada <small>opcional</small></h3>
            <p>
              Ajuda a reconhecer a marca nas matérias. Separe vários por ponto e vírgula (;) — o
              "Preencher com IA" sugere estes campos a partir do nome.
            </p>
          </div>
          <div className="onboarding-fields">
            {(
              [
                ["Apelidos e nomes anteriores", aliases, setAliases, "Ex.: Acme; Antiga Acme Elétrica"],
                ["Nomes comuns e abreviações", commonNames, setCommonNames, "Ex.: AE; Acme"],
                ["Slogans", slogans, setSlogans, "Ex.: Energia que move você"],
                ["Produtos e serviços", products, setProducts, "Ex.: Acme Solar; Tarifa Verde"],
                ["Programas e iniciativas", initiatives, setInitiatives, "Ex.: Acme nas Escolas"],
                ["Campanhas publicitárias", campaigns, setCampaigns, "Ex.: Verão Acme 2026"]
              ] as const
            ).map(([label, value, set, example]) => (
              <label className="field" key={label}>
                <span>{label}</span>
                <input value={value} onChange={e => set(e.target.value)} placeholder={example} />
              </label>
            ))}
          </div>

          </>
          )}

          <p className="onboarding-owner">
            <UserRound size={15} aria-hidden="true" />
            <span>
              <strong>{currentUserName}</strong> cria a empresa e fica com acesso total a ela.
            </span>
          </p>
          {error && <p className="form-error" role="alert">{error}</p>}
          {importNotice && (
            <p className="onboarding-tip" role="status">
              <span>{importNotice.message}</span>
              <button
                className="text-button"
                type="button"
                onClick={() =>
                  navigate(`/empresas/${importNotice.id}/configuracoes?tab=importar-configuracao`)
                }
              >
                Abrir a empresa
              </button>
            </p>
          )}

          <div className="onboarding-actions">
            <button className="secondary-button" type="button" onClick={onCancel} disabled={saving}>
              Cancelar
            </button>
            <button className="primary-button" type="submit" disabled={saving || created}>
              {saving ? (
                <LoaderCircle className="spin" size={17} />
              ) : mode === "spreadsheet" ? (
                <FileSpreadsheet size={17} />
              ) : (
                <Check size={17} />
              )}
              {saving
                ? "Criando…"
                : mode === "spreadsheet"
                  ? "Criar e importar a configuração"
                  : "Criar e abrir o assistente"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
